import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useScrollRestoration } from '../../hooks/useScrollRestoration'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import { DataTable, SortableHeader, FilterableHeader, createSelectionColumn } from '../ui/data-table'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Trash2, Eye, AlertCircle, FileText, RefreshCw } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { formatDateTimeCompact } from '../../lib/utils'
import { ALL_LANGUAGES } from '../../lib/constants/languages'

// Language codes sorted alphabetically by name
const LANGUAGE_CODES = [...ALL_LANGUAGES]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(l => l.code)

// CEFR levels
const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']

// File formats
const FILE_FORMATS = ['epub', 'fb2']

// Translation statuses
const TRANSLATION_STATUSES = ['available', 'processing', 'pending', 'failed']
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'

interface Translation {
  id: string
  book_id: string
  book_title: string
  book_author: string
  language: string
  cefr_level: string | null
  file_format: string
  status: string
  is_human_translation: boolean
  created_at: string
  updated_at: string
}

interface TranslationsTableProps {
  isActive?: boolean
}

const TRANSLATIONS_PAGE_SIZE_KEY = 'admin-translations-page-size'

export function TranslationsTable({ isActive = true }: TranslationsTableProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  useScrollRestoration('admin-translations-table', isActive)
  const [translations, setTranslations] = useState<Translation[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTranslations, setSelectedTranslations] = useState<Translation[]>([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)
  
  // Load initial pageSize from localStorage
  const getInitialPageSize = () => {
    const saved = localStorage.getItem(TRANSLATIONS_PAGE_SIZE_KEY)
    return saved ? parseInt(saved, 10) : 10
  }

  // Load initial pageIndex from URL
  const getInitialPageIndex = () => {
    const pageParam = searchParams.get('page')
    return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0
  }
  
  const [pagination, setPagination] = useState(() => ({ 
    pageIndex: getInitialPageIndex(), 
    pageSize: getInitialPageSize() 
  }))
  const [pageCount, setPageCount] = useState(0)
  
  // Server-side filtering, sorting, and search state
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [selectedLevels, setSelectedLevels] = useState<string[]>([])
  const [selectedFormats, setSelectedFormats] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }])
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Use a ref to track previous pagination to avoid unnecessary fetches
  const prevPaginationRef = useRef({ pageIndex: getInitialPageIndex(), pageSize: getInitialPageSize() })
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchValue])
  
  // Save pageSize to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(TRANSLATIONS_PAGE_SIZE_KEY, String(pagination.pageSize))
  }, [pagination.pageSize])

  // Sync pageIndex to URL
  useEffect(() => {
    const currentPage = pagination.pageIndex + 1
    const urlPage = searchParams.get('page')
    
    if (currentPage === 1) {
      // Remove page param if on first page
      if (urlPage) {
        searchParams.delete('page')
        setSearchParams(searchParams, { replace: true })
      }
    } else if (urlPage !== String(currentPage)) {
      searchParams.set('page', String(currentPage))
      setSearchParams(searchParams, { replace: true })
    }
  }, [pagination.pageIndex, searchParams, setSearchParams])

  useEffect(() => {
    // Only load if pagination values actually changed
    if (
      prevPaginationRef.current.pageIndex !== pagination.pageIndex ||
      prevPaginationRef.current.pageSize !== pagination.pageSize
    ) {
      prevPaginationRef.current = pagination
      loadTranslations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  // Load translations on initial mount
  useEffect(() => {
    loadTranslations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Reload when filters, sorting, or search change
  useEffect(() => {
    // Reset to first page when filters change
    if (pagination.pageIndex !== 0) {
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
    } else {
      loadTranslations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguages, selectedLevels, selectedFormats, selectedStatuses, sorting, debouncedSearch])

  const loadTranslations = useCallback(async () => {
    try {
      setLoading(true)
      const page = pagination.pageIndex + 1 // API uses 1-based pages
      
      // Build sort options from sorting state
      const sortBy = sorting.length > 0 ? sorting[0].id : 'created_at'
      const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : 'desc'
      
      const data = await apiClient.getAdminTranslations(page, pagination.pageSize, {
        languages: selectedLanguages.length > 0 ? selectedLanguages : undefined,
        levels: selectedLevels.length > 0 ? selectedLevels : undefined,
        formats: selectedFormats.length > 0 ? selectedFormats : undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        search: debouncedSearch || undefined,
        sortBy,
        sortOrder,
      })
      
      // Map to Translation interface
      const allTranslations: Translation[] = data.translations.map((trans: any) => ({
        id: trans.id,
        book_id: trans.book_original_id,
        book_title: trans.book_title,
        book_author: trans.book_author,
        language: trans.language,
        cefr_level: trans.cefr_level,
        file_format: trans.file_format,
        status: trans.status,
        is_human_translation: trans.is_human_translation,
        created_at: trans.created_at,
        updated_at: trans.updated_at,
      }))
      
      setTranslations(allTranslations)
      if (data.pagination) {
        setPageCount(data.pagination.total_pages)
      }
    } catch (error) {
      console.error('Failed to load translations:', error)
    } finally {
      setLoading(false)
    }
  }, [pagination.pageIndex, pagination.pageSize, selectedLanguages, selectedLevels, selectedFormats, selectedStatuses, sorting, debouncedSearch])

  const handleDeleteSelected = async () => {
    if (selectedTranslations.length === 0) return

    setDeleting(true)
    try {
      await Promise.all(
        selectedTranslations.map((trans) => apiClient.deleteTranslation(trans.id))
      )
      setShowDeleteDialog(false)
      setSelectedTranslations([])
      await loadTranslations()
    } catch (error) {
      console.error('Failed to delete translations:', error)
      alert('Failed to delete some translations. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      available: 'default',
      processing: 'secondary',
      failed: 'destructive',
      pending: 'outline',
    }
    return (
      <Badge variant={variants[status] || 'outline'} className="status-badge capitalize">
        {status}
      </Badge>
    )
  }

  const columns: ColumnDef<Translation>[] = [
    createSelectionColumn<Translation>(),
    {
      accessorKey: 'book_title',
      header: ({ column }) => <SortableHeader column={column}>Book</SortableHeader>,
      cell: ({ row }) => (
        <div className="book-info-cell max-w-xs">
          <a 
            href={`/admin/books/${row.original.book_id}?returnTab=translations`}
            className="font-medium text-foreground line-clamp-1 hover:text-primary cursor-pointer block"
            onClick={(e) => {
              // Save scroll position BEFORE navigation
              const scrollTop = window.scrollY || document.documentElement.scrollTop
              sessionStorage.setItem('scroll-position-admin-translations-table', String(scrollTop))
              // Only use SPA navigation for left clicks without modifiers
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                e.preventDefault()
                navigate(`/admin/books/${row.original.book_id}?returnTab=translations`)
              }
            }}
          >
            {row.original.book_title}
          </a>
          <div className="text-xs text-muted-foreground line-clamp-1">
            {row.original.book_author}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'language',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={LANGUAGE_CODES}
          selectedValues={selectedLanguages}
          onFilterChange={setSelectedLanguages}
          optionLabels={Object.fromEntries(ALL_LANGUAGES.map(l => [l.code, `${l.flag} ${l.name}`]))}
        >
          Language
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className="language-badge">
          {row.original.language}
        </Badge>
      ),
    },
    {
      accessorKey: 'cefr_level',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={CEFR_LEVELS}
          selectedValues={selectedLevels}
          onFilterChange={setSelectedLevels}
        >
          Level
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <span className="level-text text-sm font-medium">
          {row.original.cefr_level || 'Original'}
        </span>
      ),
    },
    {
      accessorKey: 'file_format',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={FILE_FORMATS}
          selectedValues={selectedFormats}
          onFilterChange={setSelectedFormats}
          sortable={false}
        >
          Format
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <Badge variant="secondary" className="format-badge uppercase text-xs">
          {row.original.file_format}
        </Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={TRANSLATION_STATUSES}
          selectedValues={selectedStatuses}
          onFilterChange={setSelectedStatuses}
        >
          Status
        </FilterableHeader>
      ),
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      accessorKey: 'is_human_translation',
      header: 'Type',
      cell: ({ row }) => (
        <Badge 
          variant={row.original.is_human_translation ? 'default' : 'secondary'} 
          className="type-badge text-xs"
        >
          {row.original.is_human_translation ? 'Human' : 'Auto'}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => {
        const { date, time } = formatDateTimeCompact(row.original.created_at)
        return (
          <div className="datetime-cell text-sm">
            <div>{date}</div>
            <div className="text-muted-foreground">{time}</div>
          </div>
        )
      },
    },
    {
      accessorKey: 'updated_at',
      header: ({ column }) => <SortableHeader column={column}>Updated</SortableHeader>,
      cell: ({ row }) => {
        const { date, time } = formatDateTimeCompact(row.original.updated_at)
        return (
          <div className="datetime-cell text-sm">
            <div>{date}</div>
            <div className="text-muted-foreground">{time}</div>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="actions-cell flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/books/${row.original.book_id}?returnTab=translations`)}
            title="View Book"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.original.status === 'failed' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await apiClient.getTranslationErrorLogs(row.original.id)
                    if (response.success && response.logs.length > 0) {
                      alert(`Error logs found:\n\n${response.logs.map(log => log.error_message).join('\n\n')}`)
                    } else {
                      alert('No error logs found')
                    }
                  } catch (error) {
                    alert('Failed to load error logs')
                  }
                }}
                title="View Error Log"
              >
                <FileText className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (confirm('Restart this translation job?')) {
                    try {
                      await apiClient.restartTranslationJob(row.original.id)
                      alert('Translation job restarted successfully!')
                      await loadTranslations()
                    } catch (error) {
                      alert('Failed to restart job')
                    }
                  }
                }}
                title="Restart Job"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
      enableSorting: false,
    },
  ]

  if (loading) {
    return (
      <div className="translations-table-loading flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading translations...</p>
      </div>
    )
  }

  return (
    <div className="translations-table-container space-y-4">
      {/* Bulk Actions */}
      {selectedTranslations.length > 0 && (
        <div className="bulk-actions-bar flex items-center gap-4 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="selection-count flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              {selectedTranslations.length} translation{selectedTranslations.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="bulk-action-buttons flex gap-2 ml-auto">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={translations}
        onRowSelectionChange={setSelectedTranslations}
        searchPlaceholder="Search by book title..."
        enableRowSelection={true}
        enableColumnVisibility={true}
        enablePagination={true}
        manualPagination={true}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={setPagination}
        manualSorting={true}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSearch={true}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        tableId="admin-translations"
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedTranslations.length} translation{selectedTranslations.length > 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected translations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

